import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import TypesOfArtifactOverview from '@/components/TypesOfArtifactOverview';
import PaperCard from '@/components/PaperCard';
import FiltersPanel from '@/components/FiltersPanel';
import StatsBar from '@/components/StatsBar';
import LandscapeVisualization from '@/components/LandscapeVisualization';
import AddPaperModal from '@/components/AddPaperModal';
import { loadPapers, loadConfig } from '@/lib/data';
import { matchesFacetFilters, getArtifactTypes, isAnonymized } from '@/lib/filters';

export default function PapersPage() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState(null);
  const [config, setConfig] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [facetFilters, setFacetFilters] = useState({});
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [showAddPaper, setShowAddPaper] = useState(false);

  useEffect(() => {
    Promise.all([loadPapers(), loadConfig()]).then(([p, c]) => {
      setPapers(p);
      setConfig(c);
    });
  }, []);

  // The modal stays open on its own "success" step (with the option to
  // propose the paper for the shared dataset) — this just refreshes the
  // list underneath so the new paper is already pinned at the top by the
  // time the modal closes.
  const handlePaperAdded = () => {
    loadPapers().then(setPapers);
  };

  const artifactColors = useMemo(() => {
    const map = {};
    (config?.typesOfArtifact || []).forEach((t) => {
      map[t.id] = t.color;
    });
    return map;
  }, [config]);

  const filtered = useMemo(() => {
    if (!papers) return [];
    const q = query.trim().toLowerCase();
    return papers.filter((paper) => {
      const artifactTypes = getArtifactTypes(paper);
      if (selectedTypes.length > 0 && !selectedTypes.some((t) => artifactTypes.includes(t))) {
        return false;
      }
      if (!matchesFacetFilters(paper, facetFilters)) return false;
      if (!q) return true;
      const haystack = [
        paper.metadata.title,
        ...(paper.metadata.authors || []),
        ...(paper.contribution?.keywords || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [papers, selectedTypes, facetFilters, query]);

  // Anonymized papers (outside the public sample, no title/authors) still
  // count toward the landscape/graph visualizations but are left out of the
  // browsable paper list — there's nothing to identify them by. Papers added
  // by the visitor float to the top, most-recently-added first.
  const sidebarPapers = useMemo(() => {
    const list = filtered.filter((p) => !isAnonymized(p));
    return [...list].sort((a, b) => {
      if (Boolean(a._userAdded) !== Boolean(b._userAdded)) return a._userAdded ? -1 : 1;
      if (a._userAdded && b._userAdded) return (b._userKey || '').localeCompare(a._userKey || '');
      return 0;
    });
  }, [filtered]);

  const toggleType = (id) => {
    setSelectedTypes((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const toggleFacet = (facetKey, value) => {
    setFacetFilters((prev) => {
      const current = prev[facetKey] || [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [facetKey]: next };
    });
  };

  const clearAllFilters = () => {
    setSelectedTypes([]);
    setFacetFilters({});
    setQuery('');
  };

  const toggleCompare = (id) => {
    setCompareIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const hasActiveFilters =
    selectedTypes.length > 0 || query || Object.values(facetFilters).some((v) => v && v.length > 0);

  if (!papers || !config) {
    return (
      <div className="main-layout">
        <div className="loading">
          <div className="loading__spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const labels = config.visualization?.labels || {};

  return (
    <div className="main-layout">
      <Header config={config} />
      <main className="main-content">
        <div className="papers-layout">
          <div className="papers-layout__main">
            {config.features?.stats !== false && <StatsBar papers={papers} />}

            <TypesOfArtifactOverview
              types={config.typesOfArtifact || []}
              papers={papers}
              selected={selectedTypes}
              onToggle={toggleType}
              title={labels.stepsTitle || 'Types of Artifact'}
              hint={labels.stepsHint}
            />

            {config.features?.landscape !== false && (
              <section className="landscape-section">
                <h2 className="detail-section__title">{labels.landscapeTitle || 'Research Landscape'}</h2>
                {labels.landscapeDescription && <p className="graph-page__description">{labels.landscapeDescription}</p>}
                <LandscapeVisualization papers={filtered} artifactColors={artifactColors} />
              </section>
            )}

            <div className="toolbar">
              {config.features?.search !== false && (
                <input
                  type="search"
                  placeholder="Search papers by title, author, or keyword…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="toolbar__search"
                />
              )}
              {config.features?.filters !== false && (
                <button
                  type="button"
                  className={`btn ${showFilters ? 'btn--primary' : 'btn--secondary'}`}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  Filters
                </button>
              )}
              {config.features?.compare !== false && (
                <button
                  type="button"
                  className={`btn ${compareMode ? 'btn--primary' : 'btn--secondary'}`}
                  onClick={() => {
                    setCompareMode((v) => !v);
                    setCompareIds([]);
                  }}
                >
                  {compareMode ? 'Cancel Compare' : 'Compare'}
                </button>
              )}
              {hasActiveFilters && (
                <button type="button" className="btn btn--ghost" onClick={clearAllFilters}>
                  Clear filters
                </button>
              )}
              <div className="toolbar__spacer" />
              <button type="button" className="btn btn--primary" onClick={() => setShowAddPaper(true)}>
                + Add Paper
              </button>
            </div>

            {showFilters && config.features?.filters !== false && (
              <FiltersPanel papers={papers} filters={facetFilters} onToggle={toggleFacet} onClear={() => setFacetFilters({})} />
            )}
          </div>

          <div className="papers-layout__sidebar">
            <p className="results-count">
              {sidebarPapers.length} {sidebarPapers.length === 1 ? labels.entrySingular || 'paper' : labels.entryPlural || 'papers'}
            </p>

            {sidebarPapers.length === 0 ? (
              <div className="empty-state">
                <p>No papers match the current filters.</p>
              </div>
            ) : (
              <div className="paper-list">
                {sidebarPapers.map((paper) => (
                  <PaperCard
                    key={paper._id}
                    paper={paper}
                    artifactColors={artifactColors}
                    compareMode={compareMode}
                    selected={compareIds.includes(paper._id)}
                    onToggleCompare={toggleCompare}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {compareMode && compareIds.length > 0 && (
        <div className="compare-bar">
          <span className="compare-bar__count">{compareIds.length} selected</span>
          <button className="btn btn--ghost btn--sm" onClick={() => setCompareIds([])}>Clear</button>
          <button
            className="btn btn--primary btn--sm"
            disabled={compareIds.length < 2}
            onClick={() => navigate(`/compare?ids=${compareIds.join(',')}`)}
          >
            Compare →
          </button>
        </div>
      )}

      {showAddPaper && (
        <AddPaperModal config={config} onClose={() => setShowAddPaper(false)} onAdded={handlePaperAdded} />
      )}
    </div>
  );
}
